import { Switch as BaseSwitch, useControllableState } from "tamagui";

import { TextTestID } from "@/constants/TestID";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

/**
 * @param isChecked - Controlled mode: parent explicitly controls the switch state.
 * @param isDefaultChecked - Uncontrolled mode: sets initial state, component manages itself afterwards.
 */
interface SwitchProps extends BaseViewProps {
  isChecked?: boolean;
  isDefaultChecked?: boolean;
  isDisabled?: boolean;
  onCheckedChange?: (checked: boolean) => Promise<void> | void;
}

const Switch = ({
  accessibilityLabel,
  isChecked,
  isDefaultChecked,
  isDisabled,
  onCheckedChange,
  testID,
}: SwitchProps) => {
  // "prop-wins" when controlled (isChecked provided): parent dictates state for confirmation flows.
  // "most-recent-wins" when uncontrolled (only isDefaultChecked): component self-manages after initial render.
  const [isSwitchChecked, setIsSwitchChecked] = useControllableState<boolean>({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    defaultProp: false,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prop: isChecked ?? isDefaultChecked,
    strategy: isChecked === undefined ? "most-recent-wins" : "prop-wins",
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleCheckedChange = (isChecked: boolean) => {
    setIsSwitchChecked(isChecked);
    void onCheckedChange?.(isChecked);
  };

  return (
    <BaseSwitch
      activeStyle={{
        backgroundColor: ColorToken.Primary100,
        borderColor: ColorToken.Primary100,
      }}
      aria-checked={isSwitchChecked}
      aria-label={accessibilityLabel}
      backgroundColor={ColorToken.NeutralBackgroundPrimary}
      borderColor={ColorToken.NeutralTertiary}
      borderWidth={1}
      checked={isSwitchChecked}
      disabled={isDisabled}
      height={22}
      onCheckedChange={handleCheckedChange}
      padding={2}
      // eslint-disable-next-line no-restricted-syntax -- tamagui Switch only supports $token sizes, not numbers
      size="$2"
      testID={joinTestIDs(testID, TextTestID.Switch)}
      width={40}
    >
      <BaseSwitch.Thumb
        activeStyle={{ backgroundColor: ColorToken.NeutralBackgroundPrimary }}
        backgroundColor={ColorToken.NeutralTertiary}
        height={16}
        transition="fade"
        width={16}
      />
    </BaseSwitch>
  );
};

export default Switch;
export type { SwitchProps };
